import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/primitives';
import { MIN_QUERY_LENGTH, useCompanySearch } from '@/hooks/use-company-search';
import { useSession } from '@/hooks/use-session';
import { useSite } from '@/hooks/use-site';
import { paths } from '@/lib/paths';


import {
    ArrowRight,
    Building2,
    FileCheck2,
    Info,
    Mail,
    MapPin,
    Search,
    ShieldCheck,
} from 'lucide-react';

import {
    useEffect,
    useRef,
    useState,
} from 'react';

import { useNavigate } from 'react-router-dom';

export default function HeroSection() {
    const navigate = useNavigate();
    const site = useSite();
    const { isAuthenticated, hasAccess } = useSession();

    const [query, setQuery] = useState('');
    const [showResults, setShowResults] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);

    const searchLocked =
        site.search_requires_auth && !isAuthenticated;

    /*
     * Toute la logique de recherche vient désormais du ZIP :
     * - minimum 3 caractères ;
     * - debounce 350 ms ;
     * - appel via dashlite.searchCompanies() ;
     * - réponses obsolètes ignorées ;
     * - erreurs présentées proprement.
     */
    const search = useCompanySearch(query, {
        enabled: !searchLocked,
    });

    /**
     * Ferme la liste de résultats lorsque l'utilisateur
     * clique en dehors de la zone de recherche.
     */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setShowResults(false);
            }
        };

        document.addEventListener(
            'mousedown',
            handleClickOutside,
        );

        return () => {
            document.removeEventListener(
                'mousedown',
                handleClickOutside,
            );
        };
    }, []);

    /**
     * Navigation vers la fiche entreprise du projet Dash Lite.
     */
    const handleNavigation = (
        registrationNumber: string,
    ) => {
        if (hasAccess) {
            // Utilisateur abonné -> Fiche directe
            navigate(paths.company(registrationNumber));
        } else {
            // Visiteur -> Tunnel de paiement
            navigate(paths.buy(registrationNumber));
        }
    };

    /**
     * Validation avec le bouton ou la touche Entrée.
     * Comme dans le ZIP, on ouvre le premier résultat.
     */
    const handleManualValidate = () => {
        const first = search.results[0];

        if (first) {
            handleNavigation(first.registration_number);
        }
    };

    const handleQueryChange = (value: string) => {
        setQuery(value);

        if (
            !searchLocked &&
            value.trim().length >= MIN_QUERY_LENGTH
        ) {
            setShowResults(true);
        } else {
            setShowResults(false);
        }
    };

    return (
        <section className="relative isolate min-h-[90vh] overflow-hidden py-5 text-[#0D2C4E]">
            {/* BACKGROUND */}
            <div className="absolute inset-0 -z-20">
                <img
                    src="/images/swiss-bg-2.jpeg"
                    alt=""
                    aria-hidden="true"
                    className="h-full w-full object-cover"
                />
            </div>

            {/* Halo */}
            <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,.45),transparent_55%)]" />

            <div className="relative z-10 container mx-auto mt-25 grid items-start gap-8 px-4 lg:grid-cols-12 lg:gap-12">
                {/* COLONNE CONTENU */}
                <div className="flex flex-col lg:col-span-7">
                    {/* TITRE */}
                    <div
                        className="relative mb-5 w-full"
                        style={{
                            containerType: 'inline-size',
                        }}
                    >
                        <h1
                            className="relative block w-full whitespace-nowrap font-bold leading-[1.05] tracking-[-0.03em] text-[#111820]"
                            style={{
                                fontFamily:
                                    '"Helvetica Neue", Helvetica, Arial, sans-serif',
                                fontSize:
                                    'clamp(0.95rem, 6.4cqw, 2.35rem)',
                            }}
                        >
                            Obtenez votre{' '}
                            <span className="relative inline-block text-[#b20303]">
                                Extrait RC
                            </span>{' '}
                            <span className="relative inline-block text-[#111820]">
                                en Ligne
                            </span>
                        </h1>
                    </div>

                    {/* RECHERCHE */}
                    <div
                        ref={containerRef}
                        className="relative mt-3 w-full"
                    >
                        {searchLocked ? (
                            <div className="rounded-xl border border-[#DCE2EA] bg-white p-4 text-sm text-[#6B7686] shadow-sm">
                                La recherche d'entreprises nécessite
                                une connexion sur ce site.
                            </div>
                        ) : (
                            <>
                                <form
                                    role="search"
                                    onSubmit={(event) => {
                                        event.preventDefault();
                                        handleManualValidate();
                                    }}
                                    className="flex flex-col gap-2 rounded-xl border-2 border-[#DCE2EA] bg-white p-2 shadow-[0_1px_2px_rgba(15,32,56,0.04)] transition-all duration-200 focus-within:border-[#16324F] md:flex-row md:items-center md:gap-2 md:pl-5"
                                >
                                    <div className="flex h-14 flex-1 items-center gap-3 px-3 md:h-auto md:px-0">
                                        <Search className="h-5 w-5 shrink-0 text-[#16324F]" />

                                        <input
                                            data-dashlite="company-search"
                                            type="search"
                                            className="h-full w-full bg-transparent text-base font-medium text-[#0F2038] outline-none placeholder:text-[#8892A0] md:py-4 md:text-[16px]"
                                            placeholder="Nom de la société ou numéro IDE"
                                            aria-label="Rechercher une entreprise"
                                            value={query}
                                            onChange={(event) => {
                                                handleQueryChange(
                                                    event.target.value,
                                                );
                                            }}
                                            onClick={() => {
                                                if (
                                                    query.trim().length >=
                                                    MIN_QUERY_LENGTH
                                                ) {
                                                    setShowResults(
                                                        true,
                                                    );
                                                }
                                            }}
                                        />

                                        {search.loading && (
                                            <div
                                                className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-[#DCE2EA] border-t-[#16324F]"
                                                aria-label="Recherche en cours"
                                            />
                                        )}
                                    </div>

                                    <Button
                                        data-dashlite="company-search-submit"
                                        type="submit"
                                        disabled={search.results.length === 0}
                                        className="group h-14 w-full shrink-0 cursor-pointer rounded-lg bg-[#b20303] px-7 text-[15px] font-bold text-white shadow-md transition-all duration-300 hover:-translate-y-[1px] hover:bg-[#8f0000] hover:shadow-[0_12px_28px_rgba(178,3,3,0.25)] active:scale-[0.98] disabled:opacity-100 md:h-auto md:w-auto md:py-4"
                                    >
                                        Rechercher
                                    </Button>
                                </form>

                                {/* RÉSULTATS */}
                                {showResults &&
                                    query.trim().length >=
                                        MIN_QUERY_LENGTH && (
                                        <div className="absolute top-full right-0 left-0 z-50 mt-2 overflow-hidden rounded-xl border border-[#DCE2EA] bg-white shadow-xl">
                                            <div className="max-h-[70vh] overflow-y-auto md:max-h-[380px]">
                                                {search.error ? (
                                                    <div className="p-5">
                                                        <p className="text-sm font-semibold text-[#b20303]">
                                                            {
                                                                search
                                                                    .error
                                                                    .message
                                                            }
                                                        </p>

                                                        {search.error
                                                            .hint && (
                                                            <p className="mt-1 text-xs leading-relaxed text-[#6B7686]">
                                                                {
                                                                    search
                                                                        .error
                                                                        .hint
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : search.results
                                                      .length >
                                                  0 ? (
                                                    search.results.map(
                                                        (
                                                            company,
                                                        ) => (
                                                            <button
                                                                data-dashlite="company-result"
                                                                type="button"
                                                                key={
                                                                    company.registration_number
                                                                }
                                                                onClick={() =>
                                                                    handleNavigation(
                                                                        company.registration_number,
                                                                    )
                                                                }
                                                                className="group relative flex w-full cursor-pointer items-center gap-3 border-b border-[#EDF0F4] bg-white px-3 py-3 text-left transition-colors last:border-0 hover:bg-[#F5F7FA] active:bg-[#EDF0F4] md:gap-4 md:px-4 md:py-3.5"
                                                            >
                                                                {/* Icône */}
                                                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#16324F]/[0.07] text-[#16324F] md:h-11 md:w-11">
                                                                    <Building2 className="h-5 w-5" />
                                                                </div>

                                                                {/* Informations */}
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="mb-1 flex items-center gap-2">
                                                                        <span className="truncate text-sm font-semibold text-[#0F2038] md:text-[15px]">
                                                                            {
                                                                                company.name
                                                                            }
                                                                        </span>

                                                                        <Badge
                                                                            tone="neutral"
                                                                            className="h-[18px] shrink-0 rounded px-1.5 text-[9px] font-bold uppercase md:text-[10px]"
                                                                        >
                                                                            {
                                                                                company.country
                                                                            }
                                                                        </Badge>
                                                                    </div>

                                                                    <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#6B7686] md:text-[13px]">
                                                                        <span className="font-mono">
                                                                            {
                                                                                company.registration_number
                                                                            }
                                                                        </span>

                                                                        {company.city && (
                                                                            <span className="inline-flex min-w-0 items-center gap-1">
                                                                                <MapPin className="h-3 w-3 shrink-0 opacity-60" />
                                                                                <span className="truncate">
                                                                                    {
                                                                                        company.city
                                                                                    }
                                                                                </span>
                                                                            </span>
                                                                        )}

                                                                        {company.legal_form && (
                                                                            <span className="truncate">
                                                                                {
                                                                                    company.legal_form
                                                                                }
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                </div>

                                                                <ArrowRight className="h-4 w-4 shrink-0 text-[#8892A0] opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100 md:opacity-50" />
                                                            </button>
                                                        ),
                                                    )
                                                ) : (
                                                    !search.loading &&
                                                    search.empty && (
                                                        <div className="flex min-h-[120px] flex-col items-center justify-center gap-2 p-6 text-center">
                                                            <Search className="h-8 w-8 text-[#C4CBD4]" />

                                                            <p className="text-sm font-medium text-[#6B7686]">
                                                                Aucune
                                                                entreprise
                                                                trouvée
                                                            </p>

                                                            <p className="max-w-sm text-xs text-[#9AA3AE]">
                                                                Essayez un
                                                                autre nom ou
                                                                saisissez
                                                                directement
                                                                l'identifiant
                                                                national.
                                                            </p>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )}
                            </>
                        )}
                    </div>

                    {/* ÉLÉMENTS DE CONFIANCE */}
                    <div className="my-8 w-full">
                        <div className="grid w-full grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-3 md:gap-4">
                            {[
                                {
                                    Icon: Building2,
                                    title: '630 000 +',
                                    subtitle:
                                        'Entreprises suisses répertoriées',
                                },
                                {
                                    Icon: FileCheck2,
                                    title: 'Service privé',
                                    subtitle: 'certifié',
                                },
                                {
                                    Icon: Mail,
                                    title: 'Accès 24h/24',
                                    subtitle: 'par email',
                                },
                                {
                                    Icon: Info,
                                    title: 'Infos à jour',
                                    subtitle:
                                        'Sources publiques du registre du commerce',
                                },
                            ].map(
                                ({
                                    Icon,
                                    title,
                                    subtitle,
                                }) => (
                                    <div
                                        key={title}
                                        className="flex aspect-square min-w-0 flex-col items-center justify-center gap-2 rounded-xl border border-slate-200/70 bg-white/70 p-3 text-center shadow-sm transition-shadow duration-200 hover:shadow-md"
                                        style={{
                                            containerType:
                                                'inline-size',
                                        }}
                                    >
                                        <div
                                            className="grid shrink-0 place-items-center rounded-full bg-[#b20303]/[0.05] text-[#b20303]/60"
                                            style={{
                                                width:
                                                    'clamp(36px, 20cqw, 68px)',
                                                height:
                                                    'clamp(36px, 20cqw, 68px)',
                                            }}
                                        >
                                            <Icon
                                                strokeWidth={1.5}
                                                style={{
                                                    width:
                                                        'clamp(16px, 10cqw, 30px)',
                                                    height:
                                                        'clamp(16px, 10cqw, 30px)',
                                                }}
                                            />
                                        </div>

                                        <div className="min-w-0 leading-tight">
                                            <div
                                                className="font-medium text-[#3A4657]"
                                                style={{
                                                    fontSize:
                                                        'clamp(14px, 7cqw, 26px)',
                                                }}
                                            >
                                                {title}
                                            </div>

                                            <div
                                                className="mt-0.5 font-medium tracking-tight text-slate-500"
                                                style={{
                                                    fontSize:
                                                        'clamp(11px, 5cqw, 17px)',
                                                }}
                                            >
                                                {subtitle}
                                            </div>
                                        </div>
                                    </div>
                                ),
                            )}
                        </div>
                    </div>

                    {/* IMAGE MOBILE */}
                    <div className="relative mt-3 flex min-h-[220px] w-full flex-col items-center justify-center lg:hidden">
                        <div className="relative z-10">
                            <img
                                src="/images/specimen-document.png"
                                alt="Exemple de fiche entreprise"
                                className="w-[220px] rounded-sm shadow-2xl md:w-[300px]"
                            />

                            <SealBadge className="absolute -right-3 -bottom-3 h-12 w-12 md:h-14 md:w-14" />
                        </div>

                        <div className="absolute top-1/2 left-1/2 -z-10 h-[220px] w-[220px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#16324F]/[0.08] blur-3xl" />
                    </div>

                    {/* MENTIONS LÉGALES */}
                    <div className="mt-10 max-w-[690px]">
                        <div className="mb-3 flex items-center gap-3">
                            <div className="h-px flex-1 bg-gradient-to-r from-[#d6dbe1] to-transparent" />

                            <span className="shrink-0 text-[8px] font-semibold uppercase tracking-[0.32em] text-[#B5BCC5]">
                                Mentions légales
                            </span>
                        </div>

                        <p className="text-[9px] leading-[1.1] font-light tracking-[0.005em] text-[#B3BAC3]">
                            Helvedoc.com est une plateforme privée
                            d&apos;information légale et financière,
                            totalement indépendante de tout organisme
                            public et du Registre du commerce (RC)
                            suisse. Helvedoc.com facilite l&apos;accès
                            à ses services pour les professionnels et
                            les créateurs d&apos;entreprises.
                            L&apos;utilisation de notre portail ne
                            remplace pas les documents officiels
                            délivrés par les offices cantonaux du
                            registre du commerce. Accès illimité via
                            notre forfait pro à 70 CHF par mois,
                            incluant une offre d&apos;essai à 13 CHF
                            rétractable dans les 48 heures avant le
                            premier prélèvement de l&apos;abonnement,
                            puis résiliable sans frais à tout moment
                            depuis votre espace client.
                        </p>
                    </div>
                </div>

                {/* IMAGE DESKTOP */}
                <div className="relative hidden flex-col items-center justify-center lg:sticky lg:top-24 lg:col-span-5 lg:flex lg:self-start">
                    <div className="relative z-10">
                        <div className="pointer-events-none absolute -inset-4 -z-10 rounded-2xl border border-[#b20303]/[0.10]" />

                        <img
                            src="/images/specimen-document.png"
                            alt="Exemple de fiche entreprise"
                            loading="eager"
                            fetchPriority="high"
                            width={1106}
                            height={1563}
                            className="w-[260px] rounded-sm shadow-[0_24px_50px_rgba(11,27,43,0.16),0_6px_16px_rgba(11,27,43,0.08)] xl:w-[300px]"
                        />

                        <SealBadge className="absolute -right-4 -bottom-5 h-14 w-14" />
                    </div>
                </div>
            </div>
        </section>
    );
}

function SealBadge({
    className = '',
}: {
    className?: string;
}) {
    return (
        <div
            className={`flex items-center justify-center rounded-full bg-[#F0F2F5] shadow-lg ring-4 ring-[#F5F7FA] ${className}`}
        >
            <div className="flex h-[78%] w-[78%] items-center justify-center rounded-full border border-dashed border-[#b20303]/60">
                <ShieldCheck
                    className="h-1/2 w-1/2 text-[#b20303]"
                    strokeWidth={2.4}
                />
            </div>
        </div>
    );
}